import Analytics from '@opencare/analytics';
import _ from 'lodash';

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COUNTRY } from 'common/constants';
import {
  Div,
  H1,
  MarketingButton,
  P,
  StyledFormItem,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import {
  updateCheckoutErrorStatus,
  updatePatientDetails,
} from 'connect/actions';
import { CHECKOUT_ERROR_TYPES } from 'connect/constants';
import {
  selectDataClinic,
  selectDataLoadingUser,
  selectDataPatient,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { Form } from 'opencare-crown';

import PromoSmsConsentCheckbox from 'pages/legacy-checkout/components/common/PromoSmsConsentCheckbox/PromoSmsConsentCheckbox';
import { useAnalyticsService } from 'services/AnalyticsService';

import Error from '../../../../common/components/Error/Error';
import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';
import TermsAndConditions from '../../../../common/components/TermsAndConditions/TermsAndConditions';
import { checkNumberString } from '../../../../common/helpers/form-field-helpers';
import { useFunnelStorage } from '../../../../funnel/storage/Storage';
import { StyledLock } from '../../../checkout-styled-components';
import { selectDataCheckoutFormErrorStatus } from '../../../connect/selectors';

const analytics = new Analytics();

// Component
const PhoneStep = ({ handleNextStep }) => {
  const dispatch = useDispatch();
  const funnelStorage = useFunnelStorage();
  const [phoneForm] = Form.useForm();

  const analyticsService = useAnalyticsService();

  const [formData, setFormData] = useState({});
  const [stepComplete, setStepComplete] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [promoSmsAllowed, setPromoSmsAllowed] = useState(null);

  const clinic = useSelector(selectDataClinic);
  const user = useSelector(selectDataUser);
  const patient = useSelector(selectDataPatient);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const loadingUser = useSelector(selectDataLoadingUser);
  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );

  useEffect(() => {
    if (checkoutFormErrorStatus != null) {
      setStepComplete(false);
    }
  }, [checkoutFormErrorStatus]);

  // Initial analytics page view event
  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-account',
      name: 'form-booking',
      section: 'account-phone',
      practiceId: requestedAppointment.provider?.clinic,
      providerId: requestedAppointment.providerId,
      userId: _.get(user, 'id'),
      patientId: _.get(patient, 'id'),
      funnelConfigId: _.get(funnelStorage, 'config.id'),
      ...analyticsService.analyticsData,
    });
  }, []);

  // To move to the next step – patient must be updated with phone
  useEffect(() => {
    if (stepComplete && _.get(patient, 'mobilePhone') && !loadingUser) {
      handleNextStep(formData);
    }
  }, [patient, stepComplete, loadingUser]);

  // If we already have the data, enable the button
  useEffect(() => {
    checkRequiredFields();
  }, [phoneForm.getFieldsValue()]);

  const checkRequiredFields = () => {
    setIsButtonDisabled(!phoneForm.getFieldsValue().mobilePhone);
  };

  const handlePhoneChange = () => {
    if (checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.PHONE_INVALID) {
      dispatch(updateCheckoutErrorStatus(null));
    }
  };

  const handleFormSubmit = (values) => {
    const patientDetails = {};
    patientDetails.mobilePhone = `+1${values.mobilePhone}`;

    if (typeof promoSmsAllowed === 'boolean') {
      patientDetails.settings = patient.settings || {};
      patientDetails.settings.promoSmsAllowed = promoSmsAllowed;
    }

    dispatch(updatePatientDetails(patientDetails));
    dispatch(updateCheckoutErrorStatus(null));

    analytics.trackForm({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-account',
      name: 'form-booking',
      section: 'account-phone',
      content: {
        phone: values.mobilePhone.toString(),
      },
      practiceId: requestedAppointment.provider?.clinic,
      providerId: requestedAppointment.providerId,
      userId: _.get(user, 'id'),
      patientId: _.get(patient, 'id'),
      funnelConfigId: _.get(funnelStorage, 'config.id'),
    });

    if (!loadingUser) {
      setStepComplete(true);
    }
    setFormData(values);
  };

  const renderErrorMessage = () => {
    if (
      !checkoutFormErrorStatus
      || checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.PHONE_INVALID
    ) return null;
    return (
      <Error dataTestId="ssoPhoneStepError" style={{ marginBottom: '25px' }}>
        <P color={colors.danger} data-testid={checkoutFormErrorStatus}>
          {checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.GENERIC
          && <>Something went wrong while submitting your details.</>}
        </P>
      </Error>
    );
  };

  return (
    <div>
      <H1
        data-testid="phoneStepHeading"
        fontSize="20px"
        margin="-12px 0 0 auto;"
      >
        Enter Phone Number
        <StyledLock
          aria-hidden
          bottom="5px"
          margin="0 0 0 8px"
          size="16px"
          theme="filled"
          type="lock"
          role="presentation"
        />
      </H1>
      <P color={colors.slate} margin="0 0 16px">
        Let us know how to contact you.
      </P>
      {renderErrorMessage()}
      <Form
        form={phoneForm}
        className="phone-form"
        layout="vertical"
        name="basic"
        onFinish={handleFormSubmit}
        onChange={checkRequiredFields}
        data-testid="form"
        initialValues={{
          mobilePhone: _.get(patient, 'mobilePhone')
            ? patient.mobilePhone.substring(2)
            : null,
        }}
      >
        <FloatingInput
          field="mobilePhone"
          htmlType="tel"
          form={phoneForm}
          label="Phone Number"
          required
          validator={checkNumberString}
          onChange={handlePhoneChange}
          capitalize={false}
          testId="phoneNumber"
          className="sensitive-data"
          disabled={loadingUser}
        />
        {checkoutFormErrorStatus === CHECKOUT_ERROR_TYPES.PHONE_INVALID
        && (
          <Div
            margin="-12px 0 10px"
            className="ant-form-item-explain ant-form-item-explain-error"
          >
            <div role="alert">
              The phone number is invalid, please check it and try again.
            </div>
          </Div>
        )}
        {clinic?.address?.country === COUNTRY.unitedStates
        && (
          <PromoSmsConsentCheckbox
            onChange={setPromoSmsAllowed}
            disabled={loadingUser}
          />
        )}
        <StyledFormItem marginbottom="10px">
          <MarketingButton
            type="primary"
            shape="round"
            size="large"
            block
            className="phone-form-button"
            data-testid="phoneFormButton"
            htmlType="submit"
            loading={stepComplete}
            disabled={isButtonDisabled}
          >
            Continue
          </MarketingButton>
        </StyledFormItem>
      </Form>
      <TermsAndConditions />
    </div>
  );
};

PhoneStep.propTypes = {
  handleNextStep: PropTypes.func,
};

export default PhoneStep;
